import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { TaskCheck } from '../TaskCheck'
import { TagDropdownContent } from '../TagDropdown'
import type { Tag } from '@/src/renderer/lib/db/models'
import { dateDiffDays } from '../../utils/parseStartDate'
import { useMidnightRefresh } from '../../hooks/useMidnightRefresh'
import { useTags } from '../../hooks/useTags'
import { TaskDropdown } from '../TaskDropdown'
import { TaskPlayButton } from '../TaskPlayButton'
import { TaskReactivateButton } from '../TaskReactivateButton'
import { TaskContextMenu } from '../TaskContextMenu'
import { showSuccessToast } from '@/src/renderer/components/common/SuccessToast'
import type { TaskEntryProps } from './TaskEntry.types'
import { useTaskEntryForm } from './hooks/useTaskEntryForm'
import { CollapsedProjectBadge } from './CollapsedProjectBadge'
import { CollapsedTitleIcons } from './CollapsedTitleIcons'
import { CollapsedTimings } from './CollapsedTimings'
import { ExpandedBottomBar } from './ExpandedBottomBar'
import './TaskEntry.scss'

/**
 * TaskEntry — Unified component for task display and editing.
 *
 * Three modes that morph between each other:
 * - **collapsed**: Single row (TaskCheck + title) — replaces TaskItem
 * - **expanded**: Editable card for editing existing tasks — replaces TaskCreationForm edit mode
 * - **creating**: Editable card for new tasks — replaces TaskCreationForm creation mode
 *
 * TaskCheck and title stay in the DOM across mode transitions.
 * Framer Motion `layout` animates their position changes smoothly (no fade).
 */
const TaskEntryComponent: React.FC<TaskEntryProps> = ({
  mode,
  task,
  onToggleComplete,
  onDelete,
  onDuplicate,
  onContextMenuBadgeUpdate,
  onClick,
  onDoubleClick,
  onPlay,
  onSubmitCreate,
  onSubmitEdit,
  onCancel,
  onSubmitCreateAndContinue,
  onSubmitEditAndCreateBelow,
  onNavigateNext,
  onNavigatePrev,
  onMorphComplete,
  selected = false,
  focused = false,
  multiSelected = false,
  checkType = 'task',
  checkVariant = 'default',
  autoFocus = true,
  initialExpanded = false,
  resetKey,
  startDateLabelMode,
  inProjectContext = false,
}) => {
  const { t, i18n } = useTranslation()
  const { tags: allTags } = useTags()
  const dateKey = useMidnightRefresh()

  // ── Form state + handlers (extracted hook) ──
  const form = useTaskEntryForm({
    mode, task, resetKey, selected, onClick, onCancel,
    onSubmitCreate, onSubmitEdit, onSubmitCreateAndContinue, onSubmitEditAndCreateBelow,
    onContextMenuBadgeUpdate,
  })
  const {
    title, setTitle, notes, setNotes, checked, setChecked,
    attention, setAttention, duration, setDuration,
    tags, start, setStart, deadline,
    handleAttentionChange, handleDurationChange, handleTagsChange,
    handleStartChange, handleDeadlineChange,
    saveCollapsedToDb, selectOnDropdownOpen,
    handleSubmit, handleSubmitAndCreateNew, handleSubmitRef, optimisticTitleRef,
    playSoftRemove, isExpanded,
  } = form

  // ── Collapsed dropdown states ──
  const [collapsedAttentionOpen, setCollapsedAttentionOpen] = useState(false)
  const [collapsedStartOpen, setCollapsedStartOpen] = useState(false)
  const [collapsedDurationOpen, setCollapsedDurationOpen] = useState(false)
  const [collapsedDeadlineOpen, setCollapsedDeadlineOpen] = useState(false)
  const [collapsedTagsOpen, setCollapsedTagsOpen] = useState(false)
  const [collapsedActiveTagId, setCollapsedActiveTagId] = useState<string | null>(null)
  const [isRowHovered, setIsRowHovered] = useState(false)

  // ── Right-click context menu state ──
  const [contextMenuAnchor, setContextMenuAnchor] = useState<{ x: number; y: number } | null>(null)
  // Lifted so the TaskContextMenu can imperatively open the same dropdown
  // that the trailing 3-dots Reactivate button shows.
  const [reactivateOpen, setReactivateOpen] = useState(false)

  // Used by both the direct-wake button (in-project) and the context-
  // menu's "Aufwecken" entry. Prefers the host's optimistic-update sink
  // so the row flips out of dormant state on next paint instead of
  // after the full IDB → queueSyncChange → LiveQuery roundtrip — same
  // path the right-click badge updates take.
  const handleDirectWake = useCallback(() => {
    if (!task) return
    if (onContextMenuBadgeUpdate) {
      onContextMenuBadgeUpdate(task.id, { pausedAt: null })
    } else {
      void form.saveCollapsedToDb({ pausedAt: null })
    }
  }, [task, onContextMenuBadgeUpdate, form])

  const anyDropdownOpenRef = useRef(false)
  anyDropdownOpenRef.current = collapsedAttentionOpen || collapsedStartOpen || collapsedDurationOpen || collapsedDeadlineOpen || collapsedTagsOpen

  const switchCollapsedDropdown = useCallback((target: 'attention' | 'start' | 'duration' | 'deadline' | 'tags', tagId?: string) => {
    if (!anyDropdownOpenRef.current) return
    setCollapsedAttentionOpen(target === 'attention')
    setCollapsedStartOpen(target === 'start')
    setCollapsedDurationOpen(target === 'duration')
    setCollapsedDeadlineOpen(target === 'deadline')
    setCollapsedTagsOpen(target === 'tags')
    setCollapsedActiveTagId(target === 'tags' ? (tagId ?? null) : null)
  }, [])

  // ── Refs ──
  const entryRef = useRef<HTMLDivElement>(null)
  const titleInputRef = useRef<HTMLTextAreaElement>(null)
  const notesInputRef = useRef<HTMLInputElement>(null)
  const [titleConstrained, setTitleConstrained] = useState(false)
  const wasConstrainedRef = useRef(false)
  const prevModeRef = useRef(mode)
  const justCollapsedRef = useRef(false)

  // ── Reset badge dropdowns on mode change to expanded ──
  const formInitModeRef = useRef(mode)
  if (formInitModeRef.current !== mode) {
    formInitModeRef.current = mode
    if (mode === 'expanded') {
      setCollapsedAttentionOpen(false)
      setCollapsedStartOpen(false)
      setCollapsedDurationOpen(false)
      setCollapsedDeadlineOpen(false)
    }
  }

  // ── Auto-focus title input ──
  useEffect(() => {
    if (!isExpanded || !autoFocus) return
    const timer = setTimeout(() => {
      const el = titleInputRef.current
      if (!el) return
      el.focus({ preventScroll: true })
      const len = el.value.length
      el.setSelectionRange(len, len)
    }, 50)
    return () => clearTimeout(timer)
  }, [isExpanded, autoFocus])

  // ── Title constraint during expand animation ──
  useEffect(() => {
    if (!isExpanded) { setTitleConstrained(false); return }
    setTitleConstrained(true)
    const timer = setTimeout(() => setTitleConstrained(false), 280)
    return () => clearTimeout(timer)
  }, [isExpanded])

  // ── Auto-resize title textarea ──
  useEffect(() => {
    const el = titleInputRef.current
    if (!el) return

    const wasConstrained = wasConstrainedRef.current
    wasConstrainedRef.current = titleConstrained

    let heightRafId: number | null = null
    const scheduleSimpleResize = () => {
      if (heightRafId !== null) return
      heightRafId = requestAnimationFrame(() => {
        heightRafId = null
        el.style.transition = ''
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
      })
    }

    if (titleConstrained) {
      scheduleSimpleResize()
      return () => {
        if (heightRafId !== null) cancelAnimationFrame(heightRafId)
      }
    }

    let cleanupTimer: ReturnType<typeof setTimeout> | null = null

    const releasingConstraint = wasConstrained && isExpanded
    if (releasingConstraint) {
      // Animated transition: read→write→read→write sequence is required to drive
      // the height animation (capture from, force reflow, apply transition).
      const fromHeight = el.offsetHeight
      el.style.height = 'auto'
      const toHeight = el.scrollHeight
      if (toHeight !== fromHeight) {
        el.style.height = `${fromHeight}px`
        void el.offsetHeight
        el.style.transition = 'height 0.22s cubic-bezier(0.4, 0, 0.2, 1)'
        el.style.height = `${toHeight}px`
        cleanupTimer = setTimeout(() => { el.style.transition = '' }, 240)
      } else {
        el.style.height = `${toHeight}px`
      }
    } else {
      scheduleSimpleResize()
    }

    // ResizeObserver: throttle via rAF so rapid width changes coalesce into one write
    let observerRafId: number | null = null
    let lastWidth = el.getBoundingClientRect().width
    const observer = new ResizeObserver((entries) => {
      const newWidth = entries[0].contentRect.width
      if (newWidth === lastWidth) return
      lastWidth = newWidth
      if (observerRafId !== null) return
      observerRafId = requestAnimationFrame(() => {
        observerRafId = null
        el.style.transition = ''
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
      })
    })
    observer.observe(el)

    return () => {
      if (heightRafId !== null) cancelAnimationFrame(heightRafId)
      if (observerRafId !== null) cancelAnimationFrame(observerRafId)
      if (cleanupTimer !== null) clearTimeout(cleanupTimer)
      observer.disconnect()
    }
  }, [title, isExpanded, titleConstrained])

  // ── Morph transition detection ──
  useEffect(() => {
    const prevMode = prevModeRef.current
    prevModeRef.current = mode
    if ((prevMode === 'creating' || prevMode === 'expanded') && mode === 'collapsed') {
      justCollapsedRef.current = true
      const timer = setTimeout(() => {
        justCollapsedRef.current = false
        onMorphComplete?.()
      }, 350)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [mode, onMorphComplete])

  // ── Scroll focused item into view (collapsed mode) ──
  useEffect(() => {
    if (focused && entryRef.current && !justCollapsedRef.current) {
      const el = entryRef.current
      const container = el.closest('.tab-stack__slot-scroll, .main-layout__scroll-wrap') as HTMLElement | null
      if (container) {
        const smooth = container.hasAttribute('data-smooth-scroll')
        if (smooth) container.removeAttribute('data-smooth-scroll')
        const elRect = el.getBoundingClientRect()
        const cRect = container.getBoundingClientRect()
        if (elRect.top < cRect.top || elRect.bottom > cRect.bottom) {
          el.scrollIntoView({ behavior: smooth ? 'smooth' : 'instant', block: 'nearest' })
        }
      } else {
        el.scrollIntoView({ block: 'nearest' })
      }
    }
  }, [focused])

  // ── Click outside → submit (expanded/creating modes) ──
  useEffect(() => {
    if (!isExpanded) return
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (entryRef.current?.contains(target)) return
      const el = target instanceof Element ? target : target.parentElement
      if (el?.closest('[data-radix-popper-content-wrapper]')) return
      if (el?.closest('[data-task-dropdown], [data-task-dropdown-backdrop]')) return
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return
      if (document.querySelector('[data-task-dropdown]')) return
      handleSubmitRef.current?.()
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [isExpanded])

  // ── Document-level Enter key handler ──
  useEffect(() => {
    if (!isExpanded) return
    const handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
      if (document.querySelector('[data-radix-popper-content-wrapper]')) return
      if (document.querySelector('[data-task-dropdown]')) return
      const isInsideEntry = entryRef.current?.contains(target)
      const isFocusLost = target === document.body || target === document.documentElement
      if (!isInsideEntry && !isFocusLost) return
      e.preventDefault()
      handleSubmitRef.current?.()
    }
    document.addEventListener('keydown', handleDocumentKeyDown)
    return () => document.removeEventListener('keydown', handleDocumentKeyDown)
  }, [isExpanded])

  // ── Keyboard handlers ──
  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmitAndCreateNew(); return }
    if (e.key === 'Enter') { e.preventDefault(); handleSubmit() }
    if (e.key === 'Tab' && mode === 'expanded') { e.preventDefault(); handleSubmit(); e.shiftKey ? onNavigatePrev?.() : onNavigateNext?.(); return }
    if (e.key === 'Tab' && mode === 'creating') { e.preventDefault(); return }
    if (e.key === 'Escape') { e.preventDefault(); onCancel?.() }
    if (e.key === 'ArrowDown') { e.preventDefault(); notesInputRef.current?.focus() }
  }, [handleSubmit, handleSubmitAndCreateNew, onCancel, onNavigateNext, onNavigatePrev, mode])

  const handleNotesKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSubmitAndCreateNew(); return }
    if (e.key === 'Enter') { e.preventDefault(); handleSubmit() }
    if (e.key === 'Tab' && mode === 'expanded') { e.preventDefault(); handleSubmit(); e.shiftKey ? onNavigatePrev?.() : onNavigateNext?.(); return }
    if (e.key === 'Tab' && mode === 'creating') { e.preventDefault(); return }
    if (e.key === 'Escape') { e.preventDefault(); onCancel?.() }
    if (e.key === 'ArrowUp') { e.preventDefault(); titleInputRef.current?.focus() }
  }, [handleSubmit, handleSubmitAndCreateNew, onCancel, onNavigateNext, onNavigatePrev, mode])

  const handleContainerKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!isExpanded) return
    if (e.key !== 'Enter') return
    const target = e.target as HTMLElement
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
    if (document.querySelector('[data-radix-popper-content-wrapper]')) return
    if (document.querySelector('[data-task-dropdown]')) return
    e.preventDefault()
    handleSubmit()
  }, [isExpanded, handleSubmit])

  // ── Collapsed mode handlers ──
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (task) onClick?.(task.id, e)
  }, [onClick, task])

  const handleDoubleClickEvent = useCallback(() => {
    if (task) onDoubleClick?.(task.id)
  }, [onDoubleClick, task])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (isExpanded) return
    e.preventDefault()
    if (!selected && onClick && task) onClick(task.id, e)
    const rect = entryRef.current?.getBoundingClientRect()
    if (rect) setContextMenuAnchor({ x: e.clientX, y: rect.top })
  }, [isExpanded, selected, onClick, task])

  const handleRowMouseEnter = useCallback(() => setIsRowHovered(true), [])
  const handleRowMouseLeave = useCallback(() => setIsRowHovered(false), [])
  const handleCardContextMenu = useCallback((e: React.MouseEvent) => e.stopPropagation(), [])
  const handleCheckChange = useCallback((val: boolean) => {
    if (isExpanded) setChecked(val)
    else if (task) onToggleComplete?.(task.id)
  }, [isExpanded, setChecked, task, onToggleComplete])

  // ── Computed values ──
  const displayTitle = isExpanded ? title : (optimisticTitleRef.current ?? task?.title ?? '')
  const isChecked = isExpanded ? checked : (task?.completed ?? false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [dateKey])
  const effectiveAttention = attention ?? task?.attention ?? null
  const effectiveStartDate = start?.date ?? task?.startDate ?? null
  const startDiff = effectiveStartDate ? dateDiffDays(effectiveStartDate, now) : null
  const isStartToday = startDiff === 0
  const isStartTomorrow = startDiff === 1
  const deadlineDiff = deadline ? dateDiffDays(deadline.date, now) : null
  const isDeadlineToday = deadlineDiff === 0
  const isDeadlineTomorrow = deadlineDiff === 1

  const collapsedDeadlineValue = !isExpanded && task?.dueDate
    ? { date: task.dueDate, time: task.dueTime ?? null, period: task.duePeriod ?? null }
    : null
  const collapsedDeadlineDiff = collapsedDeadlineValue ? dateDiffDays(collapsedDeadlineValue.date, now) : null
  const isCollapsedDeadlineToday = collapsedDeadlineDiff === 0
  const isCollapsedDeadlineTomorrow = collapsedDeadlineDiff === 1
  const isCollapsedDeadlineOverdue = collapsedDeadlineDiff !== null && collapsedDeadlineDiff < 0

  const collapsedTagObjects = useMemo((): Tag[] => {
    if (!task?.tags?.length) return []
    return task.tags.map(id => allTags.find(t => t.id === id)).filter((t): t is Tag => !!t)
  }, [task?.tags, allTags])
  const collapsedTagsVisible = collapsedTagObjects.length <= 3 ? collapsedTagObjects : collapsedTagObjects.slice(0, 2)
  const collapsedTagsOverflow = collapsedTagObjects.length > 3 ? collapsedTagObjects.length - 2 : 0

  const showCollapsedPlay = !isExpanded && !multiSelected && (
    isRowHovered || selected
    || collapsedAttentionOpen || collapsedStartOpen
    || collapsedDurationOpen || collapsedDeadlineOpen || collapsedTagsOpen
  )

  // Track expand↔collapse transitions for play button crossfade
  const wasExpandedRef = useRef(isExpanded)
  const collapsingFromExpanded = wasExpandedRef.current && !isExpanded
  const expandingFromCollapsed = !wasExpandedRef.current && isExpanded
  useEffect(() => { wasExpandedRef.current = isExpanded }, [isExpanded])

  // Keep ExpandedBottomBar mounted for the full duration of the close
  // animation. Without this, `{!isExpanded ? null : ...}` would rip the bar
  // out of the DOM the instant isExpanded flips to false — the outer
  // motion.div would then animate opacity/y/height on an empty wrapper, and
  // the user sees "content vanishes → empty box collapses" instead of
  // "badges slide up and fade, then the container closes".
  //
  // Must be derived synchronously in render — a useEffect-only flag would
  // be one render stale on the open transition (isExpanded true but flag
  // still false) and the bar would skip its fade-in / slide-up entirely.
  //
  // `collapsingFromExpanded` (computed above) is true during the ONE render
  // where isExpanded flips to false — that's enough to keep the bar mounted
  // while `collapseActive` (set in useEffect) catches up for the remaining
  // frames of the animation.
  const [collapseActive, setCollapseActive] = useState(false)
  useEffect(() => {
    if (isExpanded) {
      setCollapseActive(false)
      return
    }
    setCollapseActive(true)
    // Matches the bottom-wrapper close duration (~0.25 s + cushion).
    const timer = setTimeout(() => setCollapseActive(false), 280)
    return () => clearTimeout(timer)
  }, [isExpanded])
  const renderExpandedBottom = isExpanded || collapsingFromExpanded || collapseActive

  const collapsedStartValue = !isExpanded
    ? (start ?? (task?.startDate ? { date: task.startDate, time: task.startTime ?? null, period: task.startPeriod ?? null } : null))
    : null

  const anyDropdownOpen = collapsedAttentionOpen || collapsedStartOpen || collapsedDurationOpen || collapsedDeadlineOpen || collapsedTagsOpen

  // ── Restore focus to task list wrapper after closing a collapsed dropdown ──
  const prevAnyDropdownOpenRef = useRef(false)
  useEffect(() => {
    if (prevAnyDropdownOpenRef.current && !anyDropdownOpen && !isExpanded) {
      const wrapper = entryRef.current?.closest('.project-page__task-list-wrap') as HTMLElement | null
      if (wrapper) wrapper.focus()
    }
    prevAnyDropdownOpenRef.current = anyDropdownOpen
  }, [anyDropdownOpen, isExpanded])

  // ── Re-sync hover state with DOM :hover ──
  // Native mouseenter/mouseleave can miss transitions when an overlaying
  // dropdown portal (backdrop) was placed above the row during badge
  // interaction. Once the task is no longer selected and no dropdown is open,
  // verify the hover state against the actual pointer position so the play
  // button doesn't linger when the mouse is no longer over the row.
  useEffect(() => {
    if (isExpanded || anyDropdownOpen) return
    if (selected || !isRowHovered) return
    const actuallyHovered = !!entryRef.current?.matches(':hover')
    if (!actuallyHovered) setIsRowHovered(false)
  }, [selected, anyDropdownOpen, isExpanded, isRowHovered])

  const className = [
    'task-entry',
    isExpanded ? 'task-entry--expanded' : 'task-entry--collapsed',
    !isExpanded && task?.completed && 'task-entry--completed',
    !isExpanded && task?.dormant && 'task-entry--dormant',
    !isExpanded && selected && 'task-entry--selected',
    !isExpanded && focused && !selected && 'task-entry--focused',
    anyDropdownOpen && 'task-entry--dropdown-open',
  ].filter(Boolean).join(' ')

  // ── JSX ──
  const entry = (
    <div
      className={className}
      ref={entryRef}
      onClick={!isExpanded ? handleClick : undefined}
      onDoubleClick={!isExpanded ? handleDoubleClickEvent : undefined}
      onContextMenu={!isExpanded ? handleContextMenu : undefined}
      onKeyDown={handleContainerKeyDown}
      onMouseEnter={!isExpanded ? handleRowMouseEnter : undefined}
      onMouseLeave={!isExpanded ? handleRowMouseLeave : undefined}
      role={!isExpanded && onClick ? 'button' : undefined}
      tabIndex={-1}
    >
      <div
        className="task-entry__card"
        onContextMenu={isExpanded ? handleCardContextMenu : undefined}
      >
        <div className="task-entry__main">
          <div className="task-entry__check">
            <TaskCheck
              checked={isChecked}
              onChange={handleCheckChange}
              type={checkType}
              variant={checkVariant}
              disabled={mode === 'creating'}
              active={isExpanded}
              dormant={task?.dormant ?? false}
            />
          </div>

          <div className="task-entry__content">
            <div className="task-entry__title-area">
              <CollapsedTitleIcons
                isExpanded={isExpanded}
                selected={selected}
                effectiveAttention={effectiveAttention}
                effectiveStartDate={effectiveStartDate}
                startDiff={startDiff}
                collapsedAttentionOpen={collapsedAttentionOpen}
                setCollapsedAttentionOpen={setCollapsedAttentionOpen}
                collapsedStartOpen={collapsedStartOpen}
                setCollapsedStartOpen={setCollapsedStartOpen}
                collapsedStartValue={collapsedStartValue}
                locale={i18n.language}
                setAttention={setAttention}
                setStart={setStart}
                saveCollapsedToDb={saveCollapsedToDb}
                selectOnDropdownOpen={selectOnDropdownOpen}
                switchCollapsedDropdown={switchCollapsedDropdown}
                playSoftRemove={playSoftRemove}
                startDateLabelMode={startDateLabelMode}
              />
              {isExpanded ? (
                <textarea
                  ref={titleInputRef}
                  className={`task-entry__title-input${title ? ' task-entry__title-input--filled' : ''}${titleConstrained ? ' task-entry__title-input--constrained' : ''}`}
                  placeholder={t('taskCreation.titlePlaceholder')}
                  value={title}
                  rows={1}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  spellCheck={false}
                />
              ) : (
                <span className="task-entry__title">{displayTitle}</span>
              )}
              {!isExpanded && task?.projectName && (
                <CollapsedProjectBadge projectName={task.projectName} />
              )}
              <AnimatePresence>
              {!isExpanded && collapsedTagsVisible.length > 0 && (
                <motion.div
                  className="task-entry__collapsed-tags"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.12 }}
                  onClick={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                >
                  {collapsedTagsVisible.map(tag => (
                    <TaskDropdown
                      key={tag.id}
                      open={collapsedTagsOpen && collapsedActiveTagId === tag.id}
                      onOpenChange={(open) => {
                        setCollapsedTagsOpen(open)
                        setCollapsedActiveTagId(open ? tag.id : null)
                        selectOnDropdownOpen(open)
                      }}
                      side="bottom"
                      align="start"
                      alignOffset={-16}
                      trigger={
                        <button
                          className="task-entry__tag-pill"
                          type="button"
                          onMouseEnter={() => switchCollapsedDropdown('tags', tag.id)}
                        >
                          {tag.name}
                        </button>
                      }
                    >
                      <TagDropdownContent
                        variant="badge"
                        selectedIds={task?.tags ?? []}
                        onChange={(value) => saveCollapsedToDb({ tags: value })}
                        onClose={() => { setCollapsedTagsOpen(false); setCollapsedActiveTagId(null) }}
                        onRemoveAll={() => { (task?.tags ?? []).forEach((_, i) => setTimeout(() => playSoftRemove(), i * 80)); saveCollapsedToDb({ tags: [] }); setCollapsedTagsOpen(false); setCollapsedActiveTagId(null) }}
                      />
                    </TaskDropdown>
                  ))}
                  {collapsedTagsOverflow > 0 && (
                    <TaskDropdown
                      open={collapsedTagsOpen && collapsedActiveTagId === '__overflow__'}
                      onOpenChange={(open) => {
                        setCollapsedTagsOpen(open)
                        setCollapsedActiveTagId(open ? '__overflow__' : null)
                        selectOnDropdownOpen(open)
                      }}
                      side="bottom"
                      align="start"
                      alignOffset={-16}
                      trigger={
                        <button
                          className="task-entry__tag-pill task-entry__tag-pill--overflow"
                          type="button"
                          onMouseEnter={() => switchCollapsedDropdown('tags', '__overflow__')}
                        >
                          +{collapsedTagsOverflow}
                        </button>
                      }
                    >
                      <TagDropdownContent
                        variant="badge"
                        selectedIds={task?.tags ?? []}
                        onChange={(value) => saveCollapsedToDb({ tags: value })}
                        onClose={() => { setCollapsedTagsOpen(false); setCollapsedActiveTagId(null) }}
                        onRemoveAll={() => { (task?.tags ?? []).forEach((_, i) => setTimeout(() => playSoftRemove(), i * 80)); saveCollapsedToDb({ tags: [] }); setCollapsedTagsOpen(false); setCollapsedActiveTagId(null) }}
                      />
                    </TaskDropdown>
                  )}
                </motion.div>
              )}
              </AnimatePresence>
            </div>

            {/* Expandable content: notes + subtask */}
            {/* Close is snappier than open (0.18 s vs 0.25 s) so the notes row
                doesn't linger while the bottom-wrapper is already gone. */}
            <motion.div
              className="task-entry__extra"
              initial={initialExpanded ? false : { height: 0, opacity: 0 }}
              animate={{
                height: isExpanded ? 'auto' : 0,
                opacity: isExpanded ? 1 : 0,
              }}
              transition={{
                height: { duration: isExpanded ? 0.25 : 0.18, ease: [0.4, 0, 0.2, 1], delay: isExpanded ? 0 : 0.02 },
                opacity: { duration: isExpanded ? 0.2 : 0.16, ease: 'easeOut' },
              }}
            >
              <div className="task-entry__extra-inner">
                <input
                  ref={notesInputRef}
                  className="task-entry__notes"
                  type="text"
                  placeholder={t('taskCreation.notesPlaceholder')}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  onKeyDown={handleNotesKeyDown}
                  spellCheck={false}
                />
                <div className="task-entry__subtask-row">
                  <TaskCheck checked={false} onChange={() => {}} type="subtask" disabled />
                  <span className="task-entry__subtask-placeholder">
                    {t('taskCreation.subtaskPlaceholder')}
                  </span>
                </div>
              </div>
            </motion.div>
          </div>

          <AnimatePresence>
          {!isExpanded && task && (
            <motion.div
              className="task-entry__timings"
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, y: 16 }}
              transition={{ duration: 0.1, ease: [0.4, 0, 0.2, 1] }}
            >
              <CollapsedTimings
                task={task}
                collapsedDurationOpen={collapsedDurationOpen}
                setCollapsedDurationOpen={setCollapsedDurationOpen}
                collapsedDeadlineOpen={collapsedDeadlineOpen}
                setCollapsedDeadlineOpen={setCollapsedDeadlineOpen}
                collapsedDeadlineValue={collapsedDeadlineValue}
                isCollapsedDeadlineToday={isCollapsedDeadlineToday}
                isCollapsedDeadlineTomorrow={isCollapsedDeadlineTomorrow}
                isCollapsedDeadlineOverdue={isCollapsedDeadlineOverdue}
                locale={i18n.language}
                setDuration={setDuration}
                saveCollapsedToDb={saveCollapsedToDb}
                selectOnDropdownOpen={selectOnDropdownOpen}
                switchCollapsedDropdown={switchCollapsedDropdown}
                playSoftRemove={playSoftRemove}
              />
            </motion.div>
          )}
          </AnimatePresence>

          {/* Trailing slot — Play button by default, Reactivate button for
              dormant tasks. Container width animates for badge push +
              crossfade inside. */}
          {task && (
            <motion.div
              className="task-entry__play-button"
              animate={{ width: isExpanded ? (task.dormant ? 24 : 28) : showCollapsedPlay ? 24 : 0 }}
              transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
              style={{ flexShrink: 0, height: 26, position: 'relative', overflow: 'visible' }}
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            >
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    key="play-expanded"
                    initial={expandingFromCollapsed ? { opacity: 0, scale: 0.85 } : false}
                    animate={{ opacity: 1, scale: 1 }}
                    // Exit matches the bottom-wrapper close (~0.22 s) so the
                    // expanded play button fades out in step with the action
                    // bar, not 100 ms ahead of it.
                    exit={{ opacity: 0, scale: 0.70, transition: { duration: 0.13, ease: 'easeOut' } }}
                    transition={{ duration: 0.2, delay: 0.06 }}
                    style={{ position: 'absolute', top: 1, right: 0 }}
                  >
                    {task.dormant ? (
                      // In expanded mode the row already owns selection /
                      // edit focus — don't forward onClick, otherwise
                      // pressing Reactivate would also collapse the
                      // entry (and unmount this slot, killing the
                      // dropdown that just opened).
                      <TaskReactivateButton
                        task={task}
                        ariaLabel={t('dormant.reactivate')}
                        open={reactivateOpen}
                        onOpenChange={setReactivateOpen}
                        directWake={inProjectContext}
                        onDirectWake={handleDirectWake}
                      />
                    ) : (
                      <TaskPlayButton size={28} variant="expanded" onClick={() => onPlay?.(task.id)} />
                    )}
                  </motion.div>
                )}
                {showCollapsedPlay && !isExpanded && (
                  <motion.div
                    key="play-collapsed"
                    initial={collapsingFromExpanded ? { opacity: 0 } : { scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.1 } }}
                    transition={{
                      scale: { type: 'spring', stiffness: 380, damping: 22 },
                      opacity: { duration: 0.2, delay: 0.06 },
                    }}
                    style={{ position: 'absolute', top: 2, right: 0, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    {task.dormant ? (
                      <TaskReactivateButton
                        task={task}
                        ariaLabel={t('dormant.reactivate')}
                        onClick={(e) => onClick?.(task.id, e)}
                        open={reactivateOpen}
                        onOpenChange={setReactivateOpen}
                        directWake={inProjectContext}
                        onDirectWake={handleDirectWake}
                      />
                    ) : (
                      <TaskPlayButton onClick={() => onPlay?.(task.id)} />
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </div>

        {/* Bottom action bar */}
        {/* Open (total ~0.5 s — unchanged):
              – height opens  t=0      → 0.35 s
              – y slides up   t=0.05   → 0.35 s
              – opacity fades t=0.1    → 0.5 s
            Close (total ~0.25 s — snappy):
              – opacity fades t=0      → 0.22 s
              – y slides up   t=0      → 0.22 s
              – height closes t=0.03   → 0.25 s
            Close is intentionally ~half the open duration: the reveal on
            open moves only a few pixels, so mirroring that timing on close
            leaves the bar lingering visibly inside the shrinking container.
            Everything finishes together around 0.22–0.25 s so there's no
            tail where an empty box is still collapsing. */}
        <motion.div
          className="task-entry__bottom-wrapper"
          initial={initialExpanded ? false : { height: 0, opacity: 0, y: -4 }}
          animate={{
            height: isExpanded ? 'auto' : 0,
            opacity: isExpanded ? 1 : 0,
            y: isExpanded ? 0 : -20,
          }}
          transition={{
            height: { duration: isExpanded ? 0.35 : 0.22, ease: [0.4, 0, 0.2, 1], delay: isExpanded ? 0 : 0.03 },
            opacity: { duration: isExpanded ? 0.4 : 0.22, delay: isExpanded ? 0.1 : 0, ease: 'easeOut' },
            y: { duration: isExpanded ? 0.3 : 0.22, delay: isExpanded ? 0.05 : 0, ease: [0.4, 0, 0.2, 1] },
          }}
        >
          <div className="task-entry__bottom">
            {!renderExpandedBottom ? null : (
              <ExpandedBottomBar
                attention={attention}
                start={start}
                deadline={deadline}
                duration={duration}
                tags={tags}
                isStartToday={isStartToday}
                isStartTomorrow={isStartTomorrow}
                isStartOverdue={startDiff !== null && startDiff < 0}
                isDeadlineToday={isDeadlineToday}
                isDeadlineTomorrow={isDeadlineTomorrow}
                isDeadlineOverdue={deadlineDiff !== null && deadlineDiff < 0}
                handleAttentionChange={handleAttentionChange}
                handleStartChange={handleStartChange}
                handleDeadlineChange={handleDeadlineChange}
                handleDurationChange={handleDurationChange}
                handleTagsChange={handleTagsChange}
                playSoftRemove={playSoftRemove}
              />
            )}
          </div>
        </motion.div>
      </div>
    </div>
  )

  if (task && !isExpanded && onDelete) {
    const applyBadgeUpdate = (updates: Parameters<typeof saveCollapsedToDb>[0]) => {
      if (onContextMenuBadgeUpdate) {
        onContextMenuBadgeUpdate(task.id, updates)
      } else {
        saveCollapsedToDb(updates)
      }
    }
    return (
      <>
        {entry}
        <TaskContextMenu
          open={contextMenuAnchor !== null}
          onOpenChange={(o) => { if (!o) setContextMenuAnchor(null) }}
          anchorPoint={contextMenuAnchor}
          task={task}
          onStartChange={(value) => {
            setStart(value)
            applyBadgeUpdate({
              startDate: value?.date ?? null,
              startTime: value?.time ?? null,
              startPeriod: value?.period ?? null,
            })
          }}
          onDurationChange={(value) => {
            setDuration(value)
            applyBadgeUpdate({
              estimatedDuration: value?.minutes ?? null,
              durationMode: value?.mode ?? null,
            })
          }}
          onDeadlineChange={(value) => {
            applyBadgeUpdate({
              dueDate: value?.date ?? null,
              dueTime: value?.time ?? null,
              duePeriod: value?.period ?? null,
            })
          }}
          onAttentionChange={(value) => {
            setAttention(value)
            applyBadgeUpdate({ attention: value })
          }}
          onTagsChange={(value) => {
            applyBadgeUpdate({ tags: value })
          }}
          onPause={() => {
            applyBadgeUpdate({ pausedAt: new Date() })
            showSuccessToast({ message: t('taskItem.pausedToast') })
          }}
          onReactivate={() => {
            if (inProjectContext) {
              // In-project context: skip the dropdown and write the same
              // unpause delta directly via the host's optimistic sink so
              // the row flips out of dormant state on next paint. Kept
              // silent (no toast) — the visual flip is feedback enough.
              handleDirectWake()
            } else {
              setReactivateOpen(true)
            }
          }}
          onDuplicate={() => onDuplicate?.(task.id)}
          onDelete={() => onDelete?.(task.id)}
          onSubDropdownOpenChange={selectOnDropdownOpen}
        />
      </>
    )
  }

  return entry
}

// Memoized to prevent list-wide re-renders when context values tick or a sibling
// task is edited. Default shallow prop comparison is sufficient because parent
// components already pass stable callbacks (useCallback) and memoized data.
export const TaskEntry = React.memo(TaskEntryComponent)
